import Languages from '@shared/types/enums/languages';
import TranslationManager from '../assets/locale/translationsManager';

const DateUtils = {
  getCurrentDate() {
    const currentDate = new Date();
    return (
      currentDate.getFullYear() +
      '/' +
      String(currentDate.getMonth() + 1) +
      '/' +
      String(currentDate.getDate())
    );
  },
  getDateFromOffset(offset: number) {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    return (
      date.getFullYear() +
      '/' +
      String(date.getMonth() + 1) +
      '/' +
      String(date.getDate())
    );
  },
  getDurationInMinutes: (start: number, end: number) => {
    let duration = end - start;
    if (duration) {
      duration = duration / 1000 / 60;
      return Math.round(duration);
    }

    return 0;
  },
  getOffset: (start: number, end: number) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const milliseconds = endDate.getTime() - startDate.getTime();
    const seconds = Math.round(milliseconds / 1000);
    const minutes = Math.round(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    return { days, hours, minutes, seconds, milliseconds };
  },
  getOrdinalNumber: (number: number) => {
    if (TranslationManager.currentLanguage === Languages.ENGLISH) {
      const suffixes = ['th', 'st', 'nd', 'rd'];
      const remainder = number % 100;
      return (
        number +
        (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0])
      );
    }
    return number.toString();
  },
  getDayOfTheWeek(timeStamp?: number) {
    const date =
      timeStamp !== undefined && timeStamp >= 0
        ? new Date(timeStamp)
        : new Date();
    const dayOfWeek = date.getDay();
    if (dayOfWeek > -1) {
      return TranslationManager.get('MISC.DATE.DAYS.INDEX.' + dayOfWeek);
    }
    return null;
  },
  getTime({
    customDate,
    displaySeconds = true,
    displayMinutes = true,
    displayHours = true,
    displayDayOfWeek = false,
    displayDay = false,
    displayToday = true,
    displayMonth = false,
    displayMonthPreposition = false,
    displayYear = false,
    separator = ' · ',
    trimDayOfTheWeek = false,
    trimMonth = false,
    utc = false,
  }: {
    customDate?: number | string;
    displaySeconds?: boolean;
    displayMinutes?: boolean;
    displayHours?: boolean;
    displayDay?: boolean;
    displayToday?: boolean;
    displayDayOfWeek?: boolean;
    displayMonth?: boolean;
    displayMonthPreposition?: boolean;
    displayYear?: boolean;
    separator?: string;
    trimDayOfTheWeek?: boolean;
    trimMonth?: boolean;
    utc?: boolean;
  } = {}) {
    try {
      const date =
        customDate !== undefined && customDate
          ? new Date(customDate)
          : new Date();
      const isEnglish =
        TranslationManager.currentLanguage === Languages.ENGLISH;
      let currentTime = '';

      if (displayDayOfWeek) {
        const currentDate = new Date();
        const isToday =
          displayToday &&
          date.getDate() === currentDate.getDate() &&
          date.getMonth() === currentDate.getMonth() &&
          date.getFullYear() === currentDate.getFullYear();
        const dayOfWeek = utc ? date.getUTCDay() : date.getDay();
        if (dayOfWeek > -1) {
          currentTime += isToday
            ? TranslationManager.get('MISC.DATE.TODAY')
            : TranslationManager.get('MISC.DATE.DAYS.INDEX.' + dayOfWeek);
          if (trimDayOfTheWeek) {
            currentTime = currentTime.substring(0, 3);
          }
          currentTime +=
            displayDay || displayMonth || displayYear
              ? separator
              : displaySeconds || displayMinutes || displayHours
              ? ' '
              : '';
        }
      }

      if (displayDay && !isEnglish) {
        const day = utc ? date.getUTCDate() : date.getDate();
        currentTime += day + ' ';
      }

      if (displayMonth) {
        const month = utc ? date.getUTCMonth() : date.getMonth();
        if (month > -1) {
          if (displayMonthPreposition && !isEnglish) {
            currentTime +=
              TranslationManager.get('MISC.DATE.MONTHS.PREPOSITION') + ' ';
          }
          let monthLiteral = TranslationManager.get(
            'MISC.DATE.MONTHS.INDEX.' + month
          );
          if (trimMonth) {
            monthLiteral = monthLiteral.slice(0, 3);
          }
          currentTime += monthLiteral;
        }
      }

      if (displayDay && isEnglish) {
        const day = utc ? date.getUTCDate() : date.getDate();
        currentTime += ' ' + DateUtils.getOrdinalNumber(day);
      }

      if (displayMonth || (displayDay && isEnglish)) {
        currentTime +=
          (displayYear && !displayHours) ||
          (!displayYear && (displayHours || displayMinutes || displaySeconds))
            ? separator
            : displayYear
            ? ' '
            : '';
      }

      if (displayYear) {
        const year = utc ? date.getUTCFullYear() : date.getFullYear();
        currentTime += year;
        currentTime +=
          displayHours || displayMinutes || displaySeconds ? separator : '';
      }

      if (displayHours) {
        const hours = utc ? date.getUTCHours() : date.getHours();
        currentTime += hours > 9 ? hours : `0${hours}`;
      }
      if (displayMinutes) {
        const minutes = utc ? date.getUTCMinutes() : date.getMinutes();
        currentTime += ':' + (minutes > 9 ? minutes : `0${minutes}`);
      }
      if (displaySeconds) {
        const seconds = utc ? date.getUTCSeconds() : date.getSeconds();
        currentTime += ':' + (seconds > 9 ? seconds : `0${seconds}`);
      }
      return currentTime;
    } catch (e) {
      console.error(e);
      return '';
    }
  },
  getYear(date: number) {
    const currentDate = new Date(date);
    return currentDate.getFullYear();
  },
  isPastDate(date?: number) {
    if (!date) return false;
    const current = Date.now();
    return date < current;
  },
};

export default DateUtils;
